import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Mail, Phone, MapPin, Check, Archive, Inbox, Loader2, RefreshCw } from "lucide-react";

const STATUS_TABS = [
    { value: "new", label: "New", icon: Inbox },
    { value: "contacted", label: "Contacted", icon: Check },
    { value: "archived", label: "Archived", icon: Archive },
];

function formatWhen(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export default function AttorneyLeadsTab() {
    const [filter, setFilter] = useState("new");
    const [data, setData] = useState({ leads: [], counts: { new: 0, contacted: 0, archived: 0 }, has_access: false });
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/attorney/leads${filter ? `?status_filter=${filter}` : ""}`);
            setData(data);
        } catch {
            setData({ leads: [], counts: { new: 0, contacted: 0, archived: 0 }, has_access: false });
        } finally { setLoading(false); }
    }, [filter]);

    useEffect(() => { load(); }, [load]);

    const update = async (lead, status) => {
        setSavingId(lead.id);
        try {
            await api.patch(`/attorney/leads/${lead.id}`, { status });
            await load();
        } finally { setSavingId(null); }
    };

    if (!data.has_access) {
        return (
            <div className="ll-card" data-testid="leads-no-access">
                <h3 className="font-serif text-lg font-semibold mb-2">Lead inbox is a Pro / Elite feature</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                    Upgrade to <strong>Pro</strong> or <strong>Elite</strong> in any county to receive contact requests
                    from consumers who want you to follow up about their legal issue. Basic plans are phone-only.
                </p>
                <p className="text-xs text-muted-foreground">Go to your Placements tab and buy a Pro or Elite placement to activate this inbox.</p>
            </div>
        );
    }

    return (
        <div data-testid="leads-tab">
            <p className="text-sm text-muted-foreground mb-4">
                Consumers who clicked your "Request contact" form. They explicitly consented to share their info.
            </p>

            <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="leads-filters">
                {STATUS_TABS.map(({ value, label, icon: Icon }) => (
                    <button key={value} onClick={() => setFilter(value)}
                            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border transition ${
                                filter === value ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:border-primary/40"
                            }`}
                            data-testid={`leads-filter-${value}`}>
                        <Icon className="h-4 w-4" />
                        {label} <span className="opacity-70 tabular-nums">({data.counts[value] || 0})</span>
                    </button>
                ))}
                <button onClick={load} className="ml-auto text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1" data-testid="leads-refresh">
                    {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                    Refresh
                </button>
            </div>

            {!loading && data.leads.length === 0 && (
                <div className="ll-card text-sm text-muted-foreground" data-testid="leads-empty">
                    No {filter} leads yet. When consumers submit a contact request from your profile they'll appear here.
                </div>
            )}

            <div className="space-y-3" data-testid="leads-list">
                {data.leads.map((lead) => (
                    <div key={lead.id} className="ll-card" data-testid={`lead-${lead.id}`}>
                        <div className="flex items-start justify-between gap-3 mb-2">
                            <div>
                                <div className="font-serif text-base font-semibold">{lead.name}</div>
                                <div className="text-xs text-muted-foreground">{formatWhen(lead.created_at)}</div>
                            </div>
                            <span className={`ll-pill ${lead.status === "new" ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                                {lead.status}
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm mb-3">
                            <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid={`lead-email-${lead.id}`}>
                                <Mail className="h-3.5 w-3.5" /> {lead.email}
                            </a>
                            {lead.phone && (
                                <a href={`tel:${lead.phone.replace(/\D/g, "")}`} className="inline-flex items-center gap-1.5 text-primary hover:underline" data-testid={`lead-phone-${lead.id}`}>
                                    <Phone className="h-3.5 w-3.5" /> {lead.phone}
                                </a>
                            )}
                            {lead.state_code && (
                                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                    <MapPin className="h-3.5 w-3.5" /> {lead.state_code}
                                </span>
                            )}
                            {lead.preferred_contact && (
                                <span className="inline-flex items-center text-xs text-muted-foreground">
                                    Prefers: {lead.preferred_contact}
                                </span>
                            )}
                        </div>
                        <p className="text-sm whitespace-pre-wrap leading-relaxed mb-4">{lead.issue_summary}</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                            {lead.status !== "contacted" && (
                                <button onClick={() => update(lead, "contacted")} disabled={savingId === lead.id}
                                        className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-3 py-1.5 font-semibold hover:bg-primary/90 disabled:opacity-60"
                                        data-testid={`lead-mark-contacted-${lead.id}`}>
                                    <Check className="h-3.5 w-3.5" /> Mark contacted
                                </button>
                            )}
                            {lead.status !== "archived" && (
                                <button onClick={() => update(lead, "archived")} disabled={savingId === lead.id}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 hover:bg-muted/50 disabled:opacity-60"
                                        data-testid={`lead-archive-${lead.id}`}>
                                    <Archive className="h-3.5 w-3.5" /> Archive
                                </button>
                            )}
                            {lead.status !== "new" && (
                                <button onClick={() => update(lead, "new")} disabled={savingId === lead.id}
                                        className="text-muted-foreground hover:text-foreground px-3 py-1.5"
                                        data-testid={`lead-reopen-${lead.id}`}>
                                    Mark as new
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
